import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  AuthenticatedDocControllerInterface,
  DocsClientSquashVerificationObjectionMadePayload,
  DocumentState,
  EditorOrchestratorInterface,
  GeneralUserDisplayableErrorOccurredPayload,
  PublicDocumentState,
  WebsocketConnectionEventPayloads,
  EditorControllerInterface,
  GenericInfoEventPayload,
} from '@proton/docs-core'
import {
  ClientToEditorBridge,
  DocControllerEvent,
  ApplicationEvent,
  SquashVerificationObjectionDecision,
  WebsocketConnectionEvent,
  isDocumentState,
} from '@proton/docs-core'
import { DebugMenu } from './DebugMenu'
import type {
  CommentMarkNodeChangeData,
  EditorInitializationConfig,
  LiveCommentsTypeStatusChangeData,
} from '@proton/docs-shared'
import { CommentsEvent, EditorEvent, EditorSystemMode, LiveCommentsEvent } from '@proton/docs-shared'
import { EditorFrame } from '../EditorFrame'
import { mergeRegister } from '@lexical/utils'
import { useSignatureCheckFailedModal } from './SignatureCheckFailedModal'
import {
  isPrivateNodeMeta,
  isPublicNodeMeta,
  type DocumentAction,
  type NodeMeta,
  type PublicNodeMeta,
} from '@proton/drive-store'
import { c } from 'ttag'
import { useGenericAlertModal } from '@proton/docs-shared/components/GenericAlert'
import { Availability, AvailabilityTypes } from '@proton/utils/availability'
import { useGetUserSettings } from '@proton/account/userSettings/hooks'
import { WordCountOverlay } from '../WordCount'
import { useWelcomeSplashModal } from '../public/WelcomeSplashModal'
import { DocsApiErrorCode } from '@proton/shared/lib/api/docs'
import type { InviteAutoAcceptResult } from './InviteAutoAccepter'
import { InviteAutoAccepter } from './InviteAutoAccepter'
import { type DocumentError, DocumentErrorFallback } from './DocumentErrorFallback'
import { CacheService } from '@proton/docs-core/lib/Services/CacheService'
import { useAuthentication, useConfig, MimeIcon, useModalState, AuthenticatedBugModal } from '@proton/components'
import { IcLockFilled } from '@proton/icons/icons/IcLockFilled'
import { useApplication } from '~/utils/application-context'
import { AppendPublicShareKeyMaterialToTitle } from './append-public-share-key-material-to-title'
import { useFlag } from '@proton/unleash/useFlag'
import type { ProviderType } from '../../../provider-type'
import { tmpConvertNewDocTypeToOld, type DocumentType } from '@proton/drive-store/store/_documents'
import type { ProtonDocumentType } from '@proton/shared/lib/helpers/mimetype'
import { UserSettingsProvider } from '@proton/drive-store/store'
import { useDocsContext } from '../context'
import { useDebugMode } from '~/utils/debug-mode-context'
import {
  useDocsDocumentViewerEventsSDK,
  useIsSheetsEditorEnabled,
  useSharingModalDriveSdkEnabled,
  useIsOpenTracerEnabled,
} from '~/utils/flags'
import { APPS, SHEETS_APP_NAME } from '@proton/shared/lib/constants'
import { Button } from '@proton/atoms/Button/Button'
import { getAppHref } from '@proton/shared/lib/apps/helper'
import { isLocalEnvironment } from '@proton/utils/env'
import { useChangeAddressWhenPubliclyShared } from '../useChangeAddressWhenPubliclyShared'
import { generateNodeUid, getDrive, type DriveEvent, type NodeEntity } from '@proton/drive'
import { getNodeName } from '~/drive-sdk'
import { getDocsReportContextLines } from '~/utils/report-context'
import { useDriftDetectionErrorModal } from './DriftDetectionErrorModal'
import downloadFile from '@proton/shared/lib/helpers/downloadFile'
import { traceError, SentryRealtimeInitiatives } from '@proton/shared/lib/helpers/sentry'
import OpenTracer from '@proton/docs-shared/lib/Tracer/Module'
import TracerAlert from '../../../tracer/TracerAlert'
import { getEventSubscriber } from '~/drive-sdk/event-subscriber'
import { getLogsAsJSON } from '~/utils/downloadLogs'

export function useSuggestionsFeatureFlag() {
  const isDisabled = useFlag('DocsSuggestionsDisabled')
  return { isSuggestionsEnabled: !isDisabled }
}

export type DocumentViewerProps = {
  nodeMeta: NodeMeta | PublicNodeMeta
  editorInitializationConfig?: EditorInitializationConfig
  providerType: ProviderType
  openAction: DocumentAction
  actionMode: DocumentAction['mode'] | undefined
  documentType: DocumentType | ProtonDocumentType
}

export function DocumentViewer({
  nodeMeta,
  editorInitializationConfig,
  openAction,
  actionMode,
  providerType,
  documentType,
}: DocumentViewerProps) {
  const application = useApplication()
  const drive = getDrive()
  const eventSubscriber = getEventSubscriber()
  const { getLocalID } = useAuthentication()
  const getUserSettings = useGetUserSettings()
  const { isDebugMode } = useDebugMode()
  const { APP_VERSION, CLIENT_TYPE } = useConfig()

  const [documentState, setDocumentState] = useState<DocumentState | PublicDocumentState | null>(null)
  const [docController, setDocController] = useState<AuthenticatedDocControllerInterface | undefined>(undefined)
  const [editorController, setEditorController] = useState<EditorControllerInterface | null>(null)

  const [signatureFailedModal, openSignatureFailedModal] = useSignatureCheckFailedModal()
  const isSignatureFailedModalOpen = useRef(false)
  const [hasSignatureIssues, setHasSignatureIssues] = useState(false)

  const [publicSplashModal, openPublicSplashModal] = useWelcomeSplashModal()
  const [genericAlertModal, showGenericAlertModal] = useGenericAlertModal()
  const [genericInfoModal, showGenericInfoModal] = useGenericAlertModal()
  const [driftDetectionErrorModal, openDriftDetectionErrorModal] = useDriftDetectionErrorModal()

  const [editorFrame, setEditorFrame] = useState<HTMLIFrameElement | null>(null)
  const [docOrchestrator, setDocOrchestrator] = useState<EditorOrchestratorInterface | null>(null)
  const [bridge, setBridge] = useState<ClientToEditorBridge | null>(null)
  const [initializing, setInitializing] = useState(false)
  const [ready, setReady] = useState(false)
  const isSheetsEditorEnabled = useIsSheetsEditorEnabled()
  const sdkEventsEnabled = useDocsDocumentViewerEventsSDK()
  const isOpenTracerEnabled = useIsOpenTracerEnabled()

  const nodeUid = isPrivateNodeMeta(nodeMeta) ? generateNodeUid(nodeMeta.volumeId, nodeMeta.linkId) : null

  const [currentDocumentNode, setCurrentDocumentNode] = useState<NodeEntity>()
  useEffect(() => {
    if (!sdkEventsEnabled || !nodeUid) {
      return
    }

    drive
      .getNode(nodeUid)
      .then((node) => {
        setCurrentDocumentNode(node)
      })
      .catch((error) => {
        traceError(error, {
          tags: {
            initiative: SentryRealtimeInitiatives.SDK_SWITCH,
            feature: 'DocsDocumentViewerEventsSDK',
          },
        })
      })
  }, [drive, nodeUid, sdkEventsEnabled])

  const handleEvent = useCallback(
    async (event: DriveEvent) => {
      try {
        if (event.type === 'node_updated' && event.nodeUid === nodeUid && documentState) {
          if (event.isTrashed) {
            documentState.setProperty('documentTrashState', 'trashed')
            return
          }

          // Document restored from trash
          if (documentState.getProperty('documentTrashState') === 'trashed' && event.isTrashed === false) {
            documentState.setProperty('documentTrashState', 'not_trashed')
          }

          const updatedNode = await drive.getNode(nodeUid)
          const nodeName = getNodeName(updatedNode)
          if (nodeName) {
            documentState.setProperty('documentName', nodeName)
          }
        }
      } catch (error) {
        traceError(error, {
          tags: {
            initiative: SentryRealtimeInitiatives.SDK_SWITCH,
            feature: 'DocsDocumentViewerEventsSDK',
          },
        })
      }
    },
    [documentState, drive, nodeUid],
  )

  useEffect(() => {
    if (!sdkEventsEnabled || !currentDocumentNode || !documentState) {
      return
    }
    void eventSubscriber.initialize(currentDocumentNode.treeEventScopeId, [handleEvent]).catch((error) => {
      traceError(error, {
        tags: {
          initiative: SentryRealtimeInitiatives.SDK_SWITCH,
          feature: 'DocsDocumentViewerEventsSDK',
        },
      })
    })
    return () => eventSubscriber.reset()
  }, [application.logger, currentDocumentNode, documentState, eventSubscriber, handleEvent, sdkEventsEnabled])

  const [error, setError] = useState<DocumentError | null>(() => {
    if (documentType === 'sheet' && !isSheetsEditorEnabled) {
      return { message: '', code: DocsApiErrorCode.InsufficientPermissions, userUnderstandableMessage: false }
    }

    return null
  })
  const [didLoadTitle, setDidLoadTitle] = useState(false)
  const [didLoadEditorContent, setDidLoadEditorContent] = useState(false)

  const isPublicViewer = useMemo(() => documentState?.getProperty('userRole').isPublicViewer(), [documentState])
  /**
   * The editor is not allowed to change its url or systemMode after initial mount.
   * In public mode, we don't know whether the user has edit permissions until we fetch the node.
   * In private mode, we know the user's role immediately so don't have to wait for the userRole property to settle.
   */
  const renderEditor = !application.isPublicMode ? true : isPublicViewer != undefined

  const sharingModalDriveSdkEnabled = useSharingModalDriveSdkEnabled()

  useChangeAddressWhenPubliclyShared(nodeMeta, documentState)

  const { isSuggestionsEnabled } = useSuggestionsFeatureFlag()
  useEffect(() => {
    application.syncedEditorState.setProperty('suggestionsEnabled', isSuggestionsEnabled || isLocalEnvironment())
  }, [application.syncedEditorState, isSuggestionsEnabled])

  const isDownloadAction = actionMode === 'download' || openAction.mode === 'open-url-download'
  useEffect(() => {
    if (isDownloadAction && didLoadTitle && didLoadEditorContent && docOrchestrator) {
      void docOrchestrator.exportAndDownload(tmpConvertNewDocTypeToOld(documentType) === 'doc' ? 'docx' : 'xlsx')
    }
  }, [docOrchestrator, didLoadTitle, didLoadEditorContent, isDownloadAction, documentType])

  useEffect(() => {
    if (didLoadEditorContent) {
      void OpenTracer.resolveAttempt()
    }
  }, [didLoadEditorContent])

  useEffect(() => {
    if (!bridge) {
      return
    }

    if (documentState?.getProperty('userRole').isPublicViewerOrEditor()) {
      return
    }

    void getUserSettings().then((settings) => {
      void bridge.editorInvoker.loadUserSettings(settings)
    })
  }, [bridge, getUserSettings, documentState])

  useEffect(() => {
    if (!bridge) {
      return
    }

    return application.eventBus.addEventCallback(() => {
      void bridge.editorInvoker.handleCommentsChange()
    }, CommentsEvent.CommentsChanged)
  }, [application.eventBus, bridge])

  useEffect(() => {
    return documentState?.subscribeToProperty('documentName', (title) => {
      setDidLoadTitle(true)
    })
  }, [documentState])

  useEffect(() => {
    return documentState?.subscribeToEvent('EditorIsReadyToBeShown', () => {
      setDidLoadEditorContent(true)
    })
  }, [documentState])

  useEffect(() => {
    return application.eventBus.addEventCallback(() => {
      setHasSignatureIssues(true)
    }, DocControllerEvent.SquashVerificationObjectionDecisionRequired)
  }, [application.eventBus])

  useEffect(() => {
    return application.eventBus.addEventCallback(() => {
      if (isPublicViewer && editorController && documentState) {
        openPublicSplashModal({ editorController, documentState: documentState as PublicDocumentState, documentType })
      }
    }, EditorEvent.ToolbarClicked)
  }, [application.eventBus, isPublicViewer, openPublicSplashModal, editorController, documentState, documentType])

  useEffect(() => {
    return application.eventBus.addEventCallback(() => {
      showGenericAlertModal({
        title: c('Title').t`Your document cannot be loaded`,
        translatedMessage: c('Info')
          .t`Please export a copy of your current changes from the main menu and reload the page.`,
      })
    }, DocControllerEvent.UnableToResolveCommitIdConflict)
  }, [application.eventBus, showGenericAlertModal])

  useEffect(() => {
    return application.eventBus.addEventCallback((payload: GenericInfoEventPayload) => {
      showGenericInfoModal(payload)
    }, ApplicationEvent.GenericInfo)
  }, [application.eventBus, showGenericInfoModal])

  const downloadDebugInfo = useCallback(
    async (driftLogDetails?: Record<string, unknown>) => {
      if (!editorController) {
        return
      }
      let JSZip
      try {
        JSZip = (await import('jszip')).default
      } catch (error) {
        console.error('Could not import jszip', error)
        return
      }
      const zip = new JSZip()

      if (driftLogDetails) {
        try {
          zip.file('drift-log-details.json', JSON.stringify(driftLogDetails))
        } catch (error) {
          console.error('Could not include drift log details in debug info', error)
        }
      }

      const docType = tmpConvertNewDocTypeToOld(documentType)
      try {
        if (docType === 'sheet') {
          const spreadsheetPatches = await editorController.getSpreadsheetPatchesAsJsonFile()
          zip.file('spreadsheet-patches.json', spreadsheetPatches)
        }
      } catch (error) {
        console.error('Could not include spreadsheet patches in debug info', error)
      }
      try {
        if (docType === 'sheet') {
          const spreadsheetActions = await editorController.getSpreadsheetActionsAsJsonFile()
          zip.file('spreadsheet-actions.json', spreadsheetActions)
        }
      } catch (error) {
        console.error('Could not include spreadsheet actions in debug info', error)
      }

      if (docController) {
        try {
          const updatesZip = await docController.getAllUpdatesAsZip()
          zip.file('all-updates.zip', updatesZip)
        } catch (error) {
          console.error('Could not include updates file in debug info', error)
        }
      } else {
        try {
          const baseCommit = await editorController.getBaseCommitAsZip()
          zip.file('base-commit-updates.zip', baseCommit)
        } catch (error) {
          console.error('Could not include base commit in debug info', error)
        }
      }

      if (docController) {
        try {
          const { yDocJSON, sheetsJSON, editorJSON } = await getLogsAsJSON(editorController, docType)
          const updatesTimeline = await docController.getUpdatesInformationAsJsonFile(yDocJSON)
          zip.file('updates-timeline.json', updatesTimeline)
          zip.file('y-doc.json', JSON.stringify(yDocJSON))
          if (sheetsJSON) {
            zip.file('sheet-state.json', JSON.stringify(sheetsJSON))
          }
          if (editorJSON) {
            zip.file('doc-state.json', JSON.stringify(editorJSON))
          }
        } catch (error) {
          console.error('Could not include updates timeline in debug info', error)
        }
      }

      try {
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        const filename = `debug-info-${Date.now()}.zip`
        downloadFile(zipBlob, filename)
      } catch (error) {
        console.error('Could not generate zip file', error)
      }
    },
    [docController, documentType, editorController],
  )

  useEffect(() => {
    return application.eventBus.addEventCallback<GeneralUserDisplayableErrorOccurredPayload>(
      (payload: GeneralUserDisplayableErrorOccurredPayload) => {
        if (payload.irrecoverable) {
          setError({
            message: payload.translatedError,
            userUnderstandableMessage: true,
            title: payload.translatedErrorTitle,
          })
          application.metrics.reportFullyBlockingErrorModal()
          application.destroy()
          Availability.mark(AvailabilityTypes.CRITICAL)
        } else {
          showGenericAlertModal({
            title: payload.translatedErrorTitle || c('Title').t`Something went wrong`,
            translatedMessage: payload.translatedError,
            onClose: payload.onClose,
          })
        }
      },
      ApplicationEvent.GeneralUserDisplayableErrorOccurred,
    )
  }, [application, application.eventBus, showGenericAlertModal])

  const [bugReportModal, setBugReportModal, renderBugReportModal] = useModalState()
  useEffect(
    () =>
      application.eventBus.addEventCallback((driftLogDetails: Record<string, unknown>) => {
        openDriftDetectionErrorModal({
          openBugReportModal: () => setBugReportModal(true),
          downloadDebugInfo: () => downloadDebugInfo(driftLogDetails),
        })
      }, ApplicationEvent.SheetsYjsDriftDetected),
    [application.eventBus, downloadDebugInfo, setBugReportModal, openDriftDetectionErrorModal],
  )

  useEffect(() => {
    return application.eventBus.addEventCallback(() => {
      setError({
        title: c('Error').t`Unable to import file`,
        message: c('Error')
          .t`We couldn’t import this file into ${SHEETS_APP_NAME}. You can try to copy and paste the contents into a new ${SHEETS_APP_NAME}.`,
        userUnderstandableMessage: true,
        actionButton: (
          <Button
            color="norm"
            shape="outline"
            onClick={() => window.open(getAppHref('/sheet', APPS.PROTONDOCS, getLocalID()))}
          >
            {c('Action').t`Create new ${SHEETS_APP_NAME}`}
          </Button>
        ),
      })
    }, ApplicationEvent.SheetsImportErrorOccurred)
  }, [application.eventBus, getLocalID])

  useEffect(() => {
    return application.eventBus.addEventCallback(
      (payload: WebsocketConnectionEventPayloads[WebsocketConnectionEvent.EncryptionError]) => {
        showGenericAlertModal({
          title: c('Title').t`Something went wrong`,
          translatedMessage: payload.error,
        })
      },
      WebsocketConnectionEvent.EncryptionError,
    )
  }, [application.eventBus, showGenericAlertModal])

  const showFailedSignatureModal = useCallback(() => {
    isSignatureFailedModalOpen.current = true
    openSignatureFailedModal({
      ignore: () => {
        isSignatureFailedModalOpen.current = false
        const payload: DocsClientSquashVerificationObjectionMadePayload = {
          decision: SquashVerificationObjectionDecision.AbortSquash,
        }
        application.eventBus.publish({
          type: ApplicationEvent.SquashVerificationObjectionDecisionMade,
          payload,
        })
      },
      accept: () => {
        setHasSignatureIssues(false)
        isSignatureFailedModalOpen.current = false

        const payload: DocsClientSquashVerificationObjectionMadePayload = {
          decision: SquashVerificationObjectionDecision.ContinueSquash,
        }
        application.eventBus.publish({
          type: ApplicationEvent.SquashVerificationObjectionDecisionMade,
          payload,
        })
      },
    })
  }, [application.eventBus, openSignatureFailedModal])

  useEffect(() => {
    if (isSignatureFailedModalOpen.current || !hasSignatureIssues) {
      return
    }

    showFailedSignatureModal()
  }, [hasSignatureIssues, showFailedSignatureModal])

  useEffect(() => {
    if (!bridge) {
      return
    }

    return mergeRegister(
      application.eventBus.addEventCallback((data: LiveCommentsTypeStatusChangeData) => {
        void bridge.editorInvoker.handleTypingStatusChange(data.threadId)
      }, LiveCommentsEvent.TypingStatusChange),

      application.eventBus.addEventCallback((data: CommentMarkNodeChangeData) => {
        void bridge.editorInvoker.handleCreateCommentMarkNode(data.markID)
      }, CommentsEvent.CreateMarkNode),

      application.eventBus.addEventCallback((data: CommentMarkNodeChangeData) => {
        void bridge.editorInvoker.handleRemoveCommentMarkNode(data.markID)
      }, CommentsEvent.RemoveMarkNode),

      application.eventBus.addEventCallback((data: CommentMarkNodeChangeData) => {
        void bridge.editorInvoker.handleResolveCommentMarkNode(data.markID)
      }, CommentsEvent.ResolveMarkNode),

      application.eventBus.addEventCallback((data: CommentMarkNodeChangeData) => {
        void bridge.editorInvoker.handleUnresolveCommentMarkNode(data.markID)
      }, CommentsEvent.UnresolveMarkNode),
    )
  }, [application.eventBus, bridge])

  const createBridge = useCallback(
    (
      orchestrator: EditorOrchestratorInterface,
      editorFrame: HTMLIFrameElement,
      editorController: EditorControllerInterface,
    ) => {
      if (bridge) {
        application.logger.warn('Attempting to create bridge when one already exists')
        return
      }

      if (!documentState) {
        void OpenTracer.trace('boot_doc_viewer_create_bridge_document_state_not_available')
        throw new Error('Document state not yet available')
      }

      application.logger.info('Creating bridge from client to editor')
      void OpenTracer.trace('boot_doc_viewer_create_bridge_creating_bridge')

      const clientToEditorBridge = new ClientToEditorBridge(
        editorFrame,
        orchestrator,
        application.eventBus,
        application.syncedEditorState,
      )

      setBridge(clientToEditorBridge)

      void editorController.initializeEditor(
        editorInitializationConfig,
        orchestrator.userAddress,
        application.isPublicMode,
        APP_VERSION,
      )
    },
    [
      application.eventBus,
      application.isPublicMode,
      application.logger,
      application.syncedEditorState,
      bridge,
      documentState,
      editorInitializationConfig,
      APP_VERSION,
    ],
  )

  const onFrameReady = useCallback(
    (frame: HTMLIFrameElement) => {
      setEditorFrame(frame)

      application.logger.info('Editor frame ready')
    },
    [application.logger],
  )

  useEffect(() => {
    void OpenTracer.trace('boot_doc_viewer_loader_effect')

    if (docOrchestrator) {
      void OpenTracer.trace('boot_doc_viewer_loader_effect_has_doc_orchestrator')
      return
    }

    const disposer = application.getDocLoader().addStatusObserver({
      onSuccess: (result) => {
        void OpenTracer.trace('boot_doc_viewer_on_success', {
          hasDocumentState: !!result.documentState,
          hasOrchestrator: !!result.orchestrator,
          hasDocController: !!result.docController,
          hasEditorController: !!result.editorController,
        })
        setDocumentState(result.documentState)
        setDocOrchestrator(result.orchestrator)
        setDocController(result.docController)
        setEditorController(result.editorController)
        setReady(true)
        const localID = getLocalID()
        if (localID !== undefined) {
          void OpenTracer.trace('boot_doc_viewer_set_local_id_for_document_in_cache', { localID })
          CacheService.setLocalIDForDocumentInCache(nodeMeta, localID)
        }
      },
      onError: (errorMessage, code) => {
        void OpenTracer.trace('boot_doc_viewer_on_error', { code })
        setError({ message: errorMessage, userUnderstandableMessage: false, code })
        application.metrics.reportFullyBlockingErrorModal()
      },
    })

    if (!initializing) {
      setInitializing(true)
      void OpenTracer.trace('boot_doc_viewer_loader_initialize_start', { documentType })
      void application.getDocLoader().initialize(nodeMeta, tmpConvertNewDocTypeToOld(documentType))
    }

    return () => {
      void OpenTracer.trace('boot_doc_viewer_dispose')
      disposer()
    }
  }, [application, docOrchestrator, documentType, getLocalID, initializing, nodeMeta])

  useEffect(() => {
    if (docOrchestrator && editorFrame && editorController && !bridge) {
      void OpenTracer.trace('boot_doc_viewer_create_bridge')
      createBridge(docOrchestrator, editorFrame, editorController)
    }
  }, [docOrchestrator, editorFrame, createBridge, bridge, editorController])

  /**
   * If a document fails to load with insufficient permissions, we try to see if there's a pending invite for it
   * and autoaccept it, before showing any error state to the user.
   */
  const [didAttemptToAutoAcceptInvite, setDidAttemptToAutoAcceptInvite] = useState(false)
  const [wasAutoAcceptSuccessful, setWasAutoAcceptSuccessful] = useState(false)

  const onInviteAutoAcceptResult = useCallback(
    (result: InviteAutoAcceptResult) => {
      void OpenTracer.trace('boot_doc_viewer_on_invite_auto_accept_result')

      setWasAutoAcceptSuccessful(result.success)
      setDidAttemptToAutoAcceptInvite(true)

      if (result.success) {
        application.logger.info('Invite auto-accept successful, reloading page')
        void OpenTracer.trace('boot_doc_viewer_on_invite_auto_accept_result_successful')

        if (isPublicNodeMeta(nodeMeta)) {
          void OpenTracer.trace('boot_doc_viewer_invite_accept_public_redirect')
          application.compatWrapper.getPublicCompat().redirectToAuthedDocument({
            volumeId: result.acceptedNodeMeta.volumeId,
            linkId: result.acceptedNodeMeta.linkId,
          })
        } else {
          void OpenTracer.trace('boot_doc_viewer_invite_accept_reload')
          window.location.reload()
        }
      }
    },
    [application.compatWrapper, application.logger, nodeMeta],
  )

  /**
   * If we are in the public app, and the user is signed in, and the user does not have edit access, we'll check
   * to see if there is a pending invite for the document. If there is, we'll autoaccept it.
   */
  const isSignedInUserInPublicReadonlyMode =
    getLocalID() !== undefined && isPublicViewer && providerType === 'public-authenticated'
  /**
   * If we are in private mode, and the user does not have access to the document at all, we'll check
   * to see if there is a pending invite for the document. If there is, we'll autoaccept it.
   */
  const isPrivateModeUserWithInsufficientPermissions =
    isPrivateNodeMeta(nodeMeta) && error && error.code === DocsApiErrorCode.InsufficientPermissions

  const { publicContext, privateContext } = useDocsContext()

  const shouldAttemptAutoAcceptInvite =
    !didAttemptToAutoAcceptInvite &&
    (isSignedInUserInPublicReadonlyMode || isPrivateModeUserWithInsufficientPermissions)

  useEffect(() => {
    if (shouldAttemptAutoAcceptInvite) {
      void OpenTracer.trace('boot_doc_viewer_attempting_to_auto_accept_invite')
    }
  }, [shouldAttemptAutoAcceptInvite])

  useEffect(() => {
    if (wasAutoAcceptSuccessful) {
      void OpenTracer.trace('boot_doc_viewer_was_auto_accept_successful')
    }
  }, [wasAutoAcceptSuccessful])

  useEffect(() => {
    if (error) {
      void OpenTracer.trace('boot_doc_viewer_error')
    }
  }, [error])

  if (shouldAttemptAutoAcceptInvite) {
    application.logger.info('Attempting to auto-accept invite (if found)')

    return (
      <>
        <UserSettingsProvider
          initialUser={publicContext?.user ?? privateContext?.user ?? ({} as any)}
          initialDriveUserSettings={{
            Defaults: {
              RevisionRetentionDays: 0,
              PhotoTags: [],
            },
            UserSettings: {
              Sort: null,
              Layout: null,
              RevisionRetentionDays: null,
              PhotoTags: null,
            },
          }}
        >
          <InviteAutoAccepter nodeMeta={nodeMeta} onResult={onInviteAutoAcceptResult} />
        </UserSettingsProvider>
        <Loader documentType={documentType} />
      </>
    )
  }

  if (wasAutoAcceptSuccessful) {
    // Prevent flash of "Something went wrong" while page is still reloading.
    return <Loader documentType={documentType} />
  }

  if (error) {
    return <DocumentErrorFallback error={error} />
  }

  return (
    <div className="relative flex h-full w-full flex-col">
      {ready && isDebugMode && editorController && documentState && (
        <DebugMenu
          docController={docController}
          editorController={editorController}
          documentState={documentState}
          documentType={tmpConvertNewDocTypeToOld(openAction.type)}
        />
      )}

      {ready && <WordCountOverlay />}

      {(!documentState || !editorController) && <Loader documentType={documentType} />}

      {documentState &&
        isDocumentState(documentState) &&
        isPrivateNodeMeta(nodeMeta) &&
        documentState.getProperty('userRole').canReadPublicShareUrl() &&
        !sharingModalDriveSdkEnabled && (
          <AppendPublicShareKeyMaterialToTitle nodeMeta={nodeMeta} documentState={documentState} />
        )}

      {renderEditor && (
        <>
          <EditorFrame
            key="docs-editor-iframe"
            onFrameReady={onFrameReady}
            systemMode={isPublicViewer ? EditorSystemMode.PublicView : EditorSystemMode.Edit}
            logger={application.logger}
            documentType={tmpConvertNewDocTypeToOld(openAction.type)}
          />
          {isOpenTracerEnabled && <TracerAlert openBugReportModal={() => setBugReportModal(true)} />}
        </>
      )}

      {publicSplashModal}
      {signatureFailedModal}
      {genericAlertModal}
      {genericInfoModal}
      {driftDetectionErrorModal}
      {renderBugReportModal && (
        <AuthenticatedBugModal
          {...bugReportModal}
          app={APPS.PROTONDOCS}
          reportDescriptionContext={getDocsReportContextLines({
            appVersion: APP_VERSION,
            clientType: CLIENT_TYPE,
          })}
        />
      )}
    </div>
  )
}

type LoaderProps = {
  documentType: DocumentType | ProtonDocumentType
}

function Loader({ documentType }: LoaderProps) {
  return (
    <div className="relative h-full w-full">
      <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
        <MimeIcon name={documentType === 'sheet' ? 'proton-sheet' : 'proton-doc'} size={9} className="shrink-0" />

        <div className="mb-4 mt-12">
          <LoadingBar />
        </div>

        <span className="mb-2 text-center text-xl font-bold">
          {documentType === 'sheet' ? 'Proton Sheets' : 'Proton Docs'}
        </span>
        <span className="flex items-center gap-1.5">
          <IcLockFilled />
          <span className="text-center text-sm font-semibold">{c('Info').t`Decrypting your document`}</span>
        </span>
      </div>
    </div>
  )
}

function LoadingBar() {
  return (
    <div className="bg-strong h-[0.1875rem] w-72 overflow-hidden rounded-lg">
      <div className="animate-loading-bar-move h-full w-full">
        <div className="animate-loading-bar-stretch bg-primary h-full w-full rounded-lg" />
      </div>
    </div>
  )
}
